import { imsGet, resolveApiUrl } from "../api/api";
import {
  getAccessToken,
  getTenantId,
  getTenantCode,
  getEmpId,
} from "../utils/sessionDB";
import { getData, getMessage } from "../utils/responseDecoder";

const CRM12_ADD = "CRM12A";
const CRM12_READ = "CRM12S";
const CRM12_UPDATE = "CRM12U";
const GET_RESPONSE_CACHE_TTL_MS = 8000;

const inFlightRequests = new Map();
const responseCache = new Map();

function buildHeaders(serviceCode, { isJson = true, accept = "*/*" } = {}) {
  const token = getAccessToken();
  const tenantId = getTenantId();
  const tenantCode = getTenantCode();
  const empId = getEmpId();
  const authValue = token
    ? token.startsWith("Bearer ") || token.startsWith("bearer ")
      ? token
      : `Bearer ${token}`
    : "";

  return {
    ...(isJson ? { "Content-Type": "application/json" } : {}),
    Accept: accept,
    ...(authValue ? { Authorization: authValue } : {}),
    ...(tenantId ? { "X-Tenant-Id": String(tenantId) } : {}),
    ...(tenantCode ? { "X-Tenant-Code": String(tenantCode) } : {}),
    ...(empId ? { "X-Emp-Id": String(empId) } : {}),
    ...(serviceCode ? { "X-Service-Code": serviceCode } : {}),
    ...(tenantCode ? { "tenant-code": String(tenantCode) } : {}),
    ...(serviceCode ? { "service-code": serviceCode } : {}),
  };
}

export function hasLiveQuotationSession() {
  const token = String(getAccessToken() || "");
  const tenantId = String(getTenantId() || "").trim();
  const tenantCode = String(getTenantCode() || "").trim();
  const normalized = token.replace(/^Bearer\s+/i, "").trim();
  const looksLikeJwt = normalized.split(".").length >= 3;

  return Boolean(normalized) && looksLikeJwt && Boolean(tenantId) && Boolean(tenantCode);
}

function decodeResponsePayload(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (payload.encrypted === true || payload.encrypted === "true") {
    const decoded = getData(JSON.stringify(payload));
    return decoded ?? payload.data ?? payload;
  }
  return payload.data !== undefined ? payload.data : payload;
}

async function parseJsonResponse(response) {
  const text = await response.text();
  let payload = {};

  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { message: text };
    }
  }

  if (!response.ok) {
    const decodedMessage = text ? getMessage(text) : "";
    const message =
      decodedMessage ||
      payload.message ||
      payload.data?.error ||
      `${response.status} ${response.statusText}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return {
    raw: payload,
    data: decodeResponsePayload(payload),
    message: payload.message || "",
  };
}

async function request(method, path, { serviceCode, body } = {}) {
  const cacheKey = `${method}:${path}`;
  const isGet = method === "GET" && body === undefined;
  const now = Date.now();

  if (isGet) {
    const cached = responseCache.get(cacheKey);
    if (cached && now - cached.timestamp < GET_RESPONSE_CACHE_TTL_MS) {
      return cached.value;
    }

    if (inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey);
    }
  }

  const requestPromise = fetch(resolveApiUrl(path), {
    method,
    headers: buildHeaders(serviceCode),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }).then(parseJsonResponse);

  if (isGet) {
    inFlightRequests.set(cacheKey, requestPromise);
  }

  try {
    const result = await requestPromise;
    if (isGet) {
      responseCache.set(cacheKey, {
        timestamp: Date.now(),
        value: result,
      });
    }
    return result;
  } finally {
    if (isGet) {
      inFlightRequests.delete(cacheKey);
    }
  }
}

function clearQuotationCache() {
  inFlightRequests.clear();
  responseCache.clear();
}

async function requestBlob(path, { serviceCode } = {}) {
  const response = await fetch(resolveApiUrl(path), {
    method: "GET",
    headers: buildHeaders(serviceCode, { isJson: false, accept: "*/*" }),
  });

  if (!response.ok) {
    return parseJsonResponse(response);
  }

  const blob = await response.blob();
  return {
    blob,
    filename: response.headers.get("Content-Disposition") || "",
  };
}

export function mapQuotationTypeForApi(type) {
  if (type === "SITC") return "SITC";
  if (String(type || "").trim().toUpperCase() === "SERVICE") return "SERVICE";
  return "SUPPLY";
}

export async function createQuotation(payload) {
  const result = await request("POST", "/saas/crm/quotations/add", {
    serviceCode: CRM12_ADD,
    body: payload,
  });
  clearQuotationCache();
  return result;
}

export async function updateQuotation(payload) {
  const result = await request("PUT", "/saas/crm/quotations/update", {
    serviceCode: CRM12_UPDATE,
    body: payload,
  });
  clearQuotationCache();
  return result;
}

function unwrapQuotationsList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.quotations)) return payload.quotations;
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.data?.quotations)) return payload.data.quotations;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload && typeof payload === "object" && (payload.id || payload.quotationId || payload.refId)) {
    return [payload];
  }
  return [];
}


export async function getQuotations() {
  return request("GET", "/saas/crm/quotations/get", {
    serviceCode: CRM12_READ,
  });
}

/**
 * GET /saas/crm/quotations/forComplaintId/{complaintId}
 * CRM12S — quotations linked to an FSM complaint/ticket.
 */
export async function getQuotationsForComplaintId(complaintId) {
  const id = String(complaintId ?? "").trim();
  if (!id) {
    throw new Error("Complaint ID is required.");
  }
  const result = await request(
    "GET",
    `/saas/crm/quotations/forComplaintId/${encodeURIComponent(id)}`,
    { serviceCode: CRM12_READ }
  );
  return unwrapQuotationsList(result?.data ?? result);
}

export async function getQuotationById(id) {
  return request("GET", `/saas/crm/quotations/get/${id}`, {
    serviceCode: CRM12_READ,
  });
}

export async function submitQuotation(id) {
  const result = await request("POST", `/saas/crm/quotations/add/${id}/submit`, {
    serviceCode: CRM12_ADD,
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function reviseQuotation(id) {
  const result = await request("POST", `/saas/crm/quotations/add/${id}/revise`, {
    serviceCode: CRM12_ADD,
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function reviewQuotation(id, { action, managerNote = "" }) {
  const params = new URLSearchParams({
    action: String(action || "").trim(),
  });

  if (String(managerNote || "").trim()) {
    params.set("managerNote", String(managerNote).trim());
  }

  const result = await request("POST", `/saas/crm/quotations/add/${id}/review?${params.toString()}`, {
    serviceCode: CRM12_ADD,
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function updateQuotationStatus(id, { status, note = "" }) {
  const params = new URLSearchParams({
    status: String(status || "").trim(),
  });

  if (String(note || "").trim()) {
    params.set("note", String(note).trim());
  }

  const result = await request("POST", `/saas/crm/quotations/${id}/status?${params.toString()}`, {
    serviceCode: CRM12_ADD,
    body: {},
  });
  clearQuotationCache();
  return result;
}

export async function updateQuotationWorkingSheet(id, payload) {
  const result = await request("PUT", `/saas/crm/quotations/${id}/working-sheet`, {
    serviceCode: CRM12_UPDATE,
    body: payload,
  });
  clearQuotationCache();
  return result;
}

export async function downloadQuotationPdf(id) {
  return requestBlob(`/saas/crm/quotations/download/${id}/pdf`, {
    serviceCode: CRM12_READ,
  });
}

export async function getProductCatalogById(productId) {
  const normalizedId = String(productId || "").trim();
  if (!normalizedId) {
    throw new Error("Product Id is required.");
  }

  return imsGet(`/product-catalog/search/${encodeURIComponent(normalizedId)}`, "IMS04S");
}

export async function searchProductCatalog(query) {
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) {
    throw new Error("Search query is required.");
  }

  return imsGet(
    `/product-catalog/search/part-number?searchTerm=${encodeURIComponent(normalizedQuery)}&page=0&size=10&sortBy=part_number&sortDir=ASC`,
    "IMS04S"
  );
}

/** GET /saas/ims/product-catalog/search?page=0&size=10&sortBy=productName&sortDir=ASC */
export async function getProductCatalogList() {
  return imsGet(
    "/product-catalog/search?page=0&size=10&sortBy=productName&sortDir=ASC",
    "IMS04S"
  );
}

function extractCatalogProducts(response) {
  const payload = response?.data?.data ?? response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export function mapCatalogProduct(product = {}) {
  const id = product.productId ?? product.product_id ?? product.id;
  const partNumber = String(product.partNumber ?? product.part_number ?? "").trim();
  const make = String(
    product.productBrand ??
      product.product_brand ??
      product.manufacturer ??
      product.manufacturerName ??
      product.brandName ??
      ""
  ).trim();
  const name = String(product.productName ?? product.product_name ?? product.name ?? "").trim();
  const listPrice =
    Number(
      product.costPrice ??
        product.cost_price ??
        product.listPrice ??
        product.list_price ??
        0
    ) || 0;
  const uom = String(
    product.metricName ??
      product.metric_name ??
      product.unitOfMeasure ??
      product.unit_of_measure ??
      ""
  ).trim();
  const description = String(
    product.productDescription ?? product.product_description ?? product.description ?? name
  ).trim();

  return {
    id: id != null ? String(id) : partNumber,
    partNumber,
    make,
    name,
    listPrice,
    uom,
    description,
  };
}

function uniqueMakesFromProducts(products = []) {
  const seen = new Set();
  const makes = [];

  products.forEach((product) => {
    const name = String(product.make || "").trim();
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    makes.push({ id: name, name });
  });

  return makes.sort((a, b) => a.name.localeCompare(b.name));
}

/** Distinct Make options from product-catalog search (productBrand). */
export async function fetchMakesFromProductCatalog() {
  const response = await getProductCatalogList();
  const products = extractCatalogProducts(response).map(mapCatalogProduct);
  return {
    makes: uniqueMakesFromProducts(products),
    products,
  };
}

/** Part-number options for a selected Make (catalog search + part-number search). */
export async function fetchPartNumbersByMake(make, searchTerm = "") {
  const normalizedMake = String(make || "").trim().toLowerCase();
  if (!normalizedMake) return [];

  const term = String(searchTerm || "").trim();
  let products = [];

  if (term) {
    const response = await searchProductCatalog(term);
    products = extractCatalogProducts(response).map(mapCatalogProduct);
  } else {
    const response = await getProductCatalogList();
    products = extractCatalogProducts(response).map(mapCatalogProduct);
  }

  const filtered = products.filter(
    (product) =>
      product.partNumber &&
      String(product.make || "").trim().toLowerCase() === normalizedMake
  );

  const seen = new Set();
  return filtered.filter((product) => {
    const key = product.partNumber.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function fetchCatalogProductById(productId) {
  const response = await getProductCatalogById(productId);
  const products = extractCatalogProducts(response).map(mapCatalogProduct);
  if (products.length) return products[0];
  const payload = response?.data?.data ?? response?.data ?? response;
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    return mapCatalogProduct(payload);
  }
  return null;
}
